/**
 * Session-scoped reuse of already-loaded counterparty reports
 * (FEATURE-2 / CAP-F2-3: "switching between reports does not reload everything").
 *
 * Only reports live here. The list is not cached in the store: ReportList is
 * kept alive across the trip to a report (`<keep-alive>` in
 * pages/counterparty/index.vue), so it comes back with its own state and
 * revalidates itself on activation.
 *
 * The cache lives in Vuex memory only: nothing is persisted, so a browser
 * reload cold-loads, and every entry is stamped with the user it was loaded
 * for so reused content never crosses users within one SPA instance (INV-7).
 * Only a fully calculated report (status 1) is held.
 *
 * Staleness policy (OQ-33, implementer's choice): stale-while-revalidate.
 * ViewReport renders the cached report immediately and refreshes it in the
 * background; a completed edit / archive / unarchive / delete invalidates the
 * affected reports at the action layer (RULE-F2-3.5).
 *
 * Late responses: a request takes a ticket (owner, write generation and a
 * store-wide request number) when it starts and hands it back with its cache
 * write — whichever view, live or destroyed, the response lands in:
 *  - every invalidation, and a change of user, moves the generation on, so a
 *    response whose request started before it is not written;
 *  - each cache key (each report, each sources table of a report) remembers
 *    the request number of its last applied write, so the newest request wins
 *    and an older one that lands later is not written.
 */

// Route names of the list and of a report under it (fixed by INV-1).
export const LIST_ROUTE_NAME = 'counterparty'
export const REPORT_ROUTE_NAME = 'view-counterparty-report'

// Upper bound on report snapshots held per session: one full payload plus up
// to two sources pages each, so the set is capped and the least recently
// written entry is evicted first.
export const REPORT_CACHE_LIMIT = 20

export const createCacheState = () => ({
  // user key the cached content belongs to (INV-7)
  cacheOwner: null,
  // enqId -> { data, selectedToken, sources: { sof?, uof? }, savedAt, seq }
  reportCache: {},
  // monotonic write counter: orders reportCache entries for eviction
  reportCacheSeq: 0,
  // write generations, all drawn from one monotonic counter and never reset
  generationSeq: 0,
  ownerGeneration: 0,
  // enqId -> generation of its last invalidation
  reportGenerations: {},
  // store-wide request numbers (ISSUE_CACHE_REQUEST) and, per cache key, the
  // number of the last applied write; never reset
  cacheRequestSeq: 0,
  // enqId -> request number
  reportAppliedRequests: {},
  // `${enqId}:${direction}` -> request number
  sourcesAppliedRequests: {},
})

/**
 * The user key the cache is scoped to. `userData.id` is what `users/me`
 * returns for the signed-in user; `user.id` is the login-time id kept for
 * sessions where `getMe` has not resolved yet.
 */
export const cacheOwnerOf = rootState => {
  const user = (rootState && rootState.user) || {}
  const userData = user.userData || {}
  return userData.id ?? userData._id ?? user.id ?? null
}

/**
 * The route transition from an open report back to the list (RULE-F2-3.1).
 * The page uses it to let the target's `?tab` pick the tab on that return;
 * the list's own query rewrites (list → list) never do.
 */
export const isReturnFromReport = (from, to) =>
  Boolean(from && to) &&
  from.name === REPORT_ROUTE_NAME &&
  to.name === LIST_ROUTE_NAME

/**
 * Orders overlapping requests for one view. Each request takes the next
 * ticket; a response is applied only while its ticket is still the latest
 * one issued, so a slow earlier response can neither overwrite newer rows
 * nor re-cache an older payload.
 */
export const createRequestSequence = () => {
  let latest = 0
  return {
    next: () => ++latest,
    isLatest: seq => seq === latest,
  }
}

const resetCache = state => {
  state.reportCache = {}
  state.reportCacheSeq = 0
}

const nextGeneration = state => {
  state.generationSeq += 1
  return state.generationSeq
}

// One counter feeds every generation, so moving ownerGeneration on outdates
// every report ticket at once.
const reportGenerationOf = (state, enqId) =>
  Math.max(state.ownerGeneration, state.reportGenerations[enqId] || 0)

// A write without a generation is unguarded (direct commits); the views pass
// the generation of the ticket their request took when it started.
const isCurrentGeneration = (current, generation) =>
  generation === undefined || generation === current

// Newest request wins per cache key: a write is dropped only when a strictly
// newer request has already been applied for that key. Equal passes on
// purpose — it is the same request writing again (the report's entity
// enrichment re-commits with the ticket of the request it belongs to).
const isNewestRequest = (lastApplied = 0, request) =>
  request === undefined || request >= lastApplied

const sourcesKey = (enqId, direction) => `${enqId}:${direction}`

// A write for a different user drops everything held for the previous one and
// outdates the writes still pending for it (claiming an empty cache does not).
const ensureOwner = (state, owner = null) => {
  if (state.cacheOwner !== owner) {
    if (state.cacheOwner !== null) state.ownerGeneration = nextGeneration(state)
    resetCache(state)
    state.cacheOwner = owner
  }
}

const isOwnedBy = (state, rootState) =>
  state.cacheOwner === cacheOwnerOf(rootState)

// Drops the least recently written entries until the cap holds.
const withinLimit = (reportCache, limit = REPORT_CACHE_LIMIT) => {
  const ids = Object.keys(reportCache)
  if (ids.length <= limit) return reportCache
  const next = { ...reportCache }
  ids
    .sort((a, b) => (next[a].seq || 0) - (next[b].seq || 0))
    .slice(0, ids.length - limit)
    .forEach(id => delete next[id])
  return next
}

export const cacheMutations = {
  // Numbers the request that is about to start; the view then takes its
  // ticket (reportCacheTicket), which carries this number.
  ISSUE_CACHE_REQUEST(state) {
    state.cacheRequestSeq += 1
  },
  SET_REPORT_CACHE(
    state,
    { owner = null, generation, request, enqId, data, selectedToken },
  ) {
    if (!enqId || !data) return
    // Only a fully calculated report is reused. A report still calculating
    // (status 2, "No data here yet", sources not ready) would reopen on a
    // stale intermediate state without the loader, take a slot from a
    // finished report under REPORT_CACHE_LIMIT, and be re-polled anyway, so
    // it always cold-loads with the loader and polling, as on develop.
    if (data.status != 1) return
    if (!isCurrentGeneration(reportGenerationOf(state, enqId), generation)) return
    if (!isNewestRequest(state.reportAppliedRequests[enqId], request)) return
    ensureOwner(state, owner)
    if (request !== undefined) {
      state.reportAppliedRequests = {
        ...state.reportAppliedRequests,
        [enqId]: request,
      }
    }
    const previous = state.reportCache[enqId] || {}
    state.reportCacheSeq += 1
    state.reportCache = withinLimit({
      ...state.reportCache,
      [enqId]: {
        // shallow copy: later in-place enrichment of the payload by the view
        // does not mutate store state; the view re-commits when it finishes
        data: { ...data },
        selectedToken: { ...(selectedToken || {}) },
        sources: previous.sources || {},
        savedAt: Date.now(),
        // a re-written entry becomes the most recent one
        seq: state.reportCacheSeq,
      },
    })
  },
  // First page of a sources table in the default view state (page 1, no
  // filters, no sort, no active bar item). Attaches to an existing report
  // entry only; sources without their report are never reused.
  SET_REPORT_SOURCES_CACHE(
    state,
    {
      owner = null,
      generation,
      request,
      enqId,
      direction,
      list,
      totalLoaded = 0,
      hasMore = false,
    },
  ) {
    if (!enqId || !list || (direction !== 'sof' && direction !== 'uof')) return
    if (!isCurrentGeneration(reportGenerationOf(state, enqId), generation)) return
    const key = sourcesKey(enqId, direction)
    if (!isNewestRequest(state.sourcesAppliedRequests[key], request)) return
    if (state.cacheOwner !== owner) return
    const entry = state.reportCache[enqId]
    if (!entry) return
    if (request !== undefined) {
      state.sourcesAppliedRequests = {
        ...state.sourcesAppliedRequests,
        [key]: request,
      }
    }
    state.reportCache = {
      ...state.reportCache,
      [enqId]: {
        ...entry,
        sources: {
          ...entry.sources,
          [direction]: { list: { ...list }, totalLoaded, hasMore },
        },
      },
    }
  },
  INVALIDATE_REPORT_CACHE(state, { enqId } = {}) {
    if (!enqId) return
    // outdates the writes still pending for this report, cached or not
    state.reportGenerations = {
      ...state.reportGenerations,
      [enqId]: nextGeneration(state),
    }
    if (!state.reportCache[enqId]) return
    const next = { ...state.reportCache }
    delete next[enqId]
    state.reportCache = next
  },
  CLEAR_COUNTERPARTY_CACHE(state) {
    resetCache(state)
    state.cacheOwner = null
    state.ownerGeneration = nextGeneration(state)
  },
}

export const cacheGetters = {
  cacheOwnerKey: (state, getters, rootState) => cacheOwnerOf(rootState),
  // Taken right after ISSUE_CACHE_REQUEST when a request starts, and handed
  // back with its cache write: the user it was made for, the write generation
  // and the request number at that moment.
  reportCacheTicket: (state, getters, rootState) => enqId => ({
    owner: cacheOwnerOf(rootState),
    generation: reportGenerationOf(state, enqId),
    request: state.cacheRequestSeq,
  }),
  cachedReport: (state, getters, rootState) => enqId => {
    if (!enqId || !isOwnedBy(state, rootState)) return null
    return state.reportCache[enqId] || null
  },
}
